import React, { useEffect, useMemo, useState } from 'react';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from '../../../helpers/pagination-helper.ts';
import {
  AdvancedFormReturnType,
  useAdvancedForm,
} from '../../../helpers/form/useAdvancedForm.ts';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import { useWatch } from 'react-hook-form';
import { Filters } from './components/Filters.tsx';
import styles from './ServiceAndRepair.module.scss';
import {
  ServiceAndRepairDocument,
  ServiceAndRepairQuery,
} from 'services/api/api-client.ts';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { AppPagination } from '../../../components/uikit/pagination/AppPagination.tsx';
import { AccordionHeader } from '../../../components/uikit/AccordionHeader.tsx';
import { Accordion } from './components/Accordion.tsx';
import { useAnalytics } from 'helpers/analytics.ts';
import { abasUnavailableUrn, ErrObj } from '../../../helpers/abas-helper.ts';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';

export const ServiceAndRepairCategory = 'Service and Repair';

export type FormParams = {
  id: number | null;
  name: string;
  language: string;
};
export const Id = 'Id';

export type FilterProps = {
  form: AdvancedFormReturnType<FormParams>;
  isResetButtonShow: boolean;
  resetFilters: () => void;
  id: number | null;
  name: string;
  language: string;
  languages: string[];
  isSelectOpened: boolean;
  changeSelectState: (value: boolean) => void;
};

export type GroupedData = Record<string, ServiceAndRepairDocument[]>;

export type PaginationProps = {
  page: number;
  perPage: number;
  totalCount: number;
  pageSizes: number[];
  changePagination: (pagination: { page?: number; perPage?: number }) => void;
};

const columnsOfOuterTable: {
  translateKey: string;
  serverKey: string;
  width: number | string;
  classname?: string;
  isSortingTurnedOff?: boolean;
}[] = [
  {
    serverKey: Id,
    translateKey: 'id',
    width: 68,
  },
  {
    serverKey: 'name',
    translateKey: 'name',
    width: 'unset',
    classname: styles.flex1,
  },
  {
    serverKey: '',
    translateKey: 'german',
    width: 88,
    isSortingTurnedOff: true,
  },
  {
    serverKey: '',
    translateKey: 'english',
    width: 88,
    isSortingTurnedOff: true,
  },
  {
    serverKey: '',
    translateKey: 'french',
    width: 88,
    isSortingTurnedOff: true,
  },
  {
    serverKey: '',
    translateKey: 'italian',
    width: 88,
    isSortingTurnedOff: true,
  },
  {
    serverKey: '',
    translateKey: 'spanish',
    width: 88,
    isSortingTurnedOff: true,
  },
  {
    serverKey: '',
    translateKey: 'undefined',
    width: 104,
    isSortingTurnedOff: true,
  },
];

const pageSizes = [10, 20, 50];

export const ServiceAndRepair = () => {
  const i18n = useScopedTranslation('Page.ServiceAndRepair');
  const { trackEvent } = useAnalytics();

  const [isSelectOpened, setIsSelectOpened] = useState(false);
  const [closedOrders, setClosedOrders] = useState<Set<string>>(new Set([]));
  const [hoveredHeader, setHoveredHeader] = useState<string | null>(null);

  const changeSelectState = (value: boolean) => {
    setIsSelectOpened(value);
  };

  const languages = [
    'all',
    'german',
    'english',
    'french',
    'italian',
    'spanish',
    'undefined',
  ];

  const [params, setParams] = useQueryParams({
    id: NumberParam,
    name: StringParam,
    language: StringParam,
    ...pagingSortingQueryParams(pageSizes[0]),
    sortBy: withDefault(StringParam, Id),
    desc: withDefault(BooleanParam, false),
  });

  const form = useAdvancedForm<FormParams>(
    async (data) => {
      setParams({
        ...params,
        ...data,
        language: data.language === languages[0] ? undefined : data.language,
        page: 1,
      });

      openAllAccordions();
    },
    {
      defaultValues: {
        name: params.name ?? '',
        id: params.id ?? null,
        language: params.language ?? languages[0],
        ...pagingSortingToBackendRequest(params),
      },
    },
  );

  const [name, id, language] = useWatch({
    control: form.control,
    name: ['name', 'id', 'language'],
  });

  const serviceAndRepairQuery = ServiceAndRepairQuery.useGetQuery(
    {
      id: params.id ?? undefined,
      name: params.name ? params.name.trim() : '',
      language: params.language ?? '',
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  const data = serviceAndRepairQuery.data;

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));

    openAllAccordions();
  };

  useEffect(() => {
    document.body.onscroll = () => {
      setIsSelectOpened(false);
    };

    return () => {
      document.body.onscroll = null;
    };
  }, []);

  const resetFilters = () => {
    form.reset({
      name: '',
      id: null,
      language: languages[0],
    });

    void form.handleSubmitDefault();
  };

  const isResetButtonShow = !!params.id || !!params.name || !!params.language;

  const filterProps: FilterProps = {
    form,
    resetFilters,
    id,
    language,
    name,
    languages,
    isResetButtonShow,
    isSelectOpened,
    changeSelectState,
  };

  const numberOfRows = data?.serviceAndRepairDocuments.length ?? 0;
  const totalCount = data?.countOfRecordWithFilters;

  const paginationProps = useMemo(
    () => ({
      page: params.page,
      perPage: params.perPage,
      totalCount: totalCount ?? 0,
      pageSizes: pageSizes,
      numberOfRows,
      changePagination: changePagination,
    }),
    [params, pageSizes, totalCount, numberOfRows],
  );

  const changeSortTable = (key: string) => {
    const previousKey = params.sortBy;

    setParams((prevParams) => ({
      ...prevParams,
      sortBy: key,
      desc: previousKey === key ? !params.desc : false,
      page: 1,
    }));

    openAllAccordions();
  };

  const groupedData = useMemo(() => {
    const result: GroupedData = {};

    if (!data) return result;

    for (const dataItem of data.serviceAndRepairDocuments) {
      const key = '_' + dataItem.groupId;

      if (!result[key]) {
        result[key] = [dataItem];
      } else {
        result[key] = [...result[key], dataItem];
      }
    }

    return result;
  }, [data]);

  const handleClickAccordion = (key: string) => {
    const copy = new Set(closedOrders);

    trackEvent({
      category: ServiceAndRepairCategory,
      action: 'Click table row group',
      name: `Table group key: ${key}`,
    });

    if (copy.has(key)) {
      copy.delete(key);
    } else {
      copy.add(key);
    }

    setClosedOrders(copy);
  };

  const openAllAccordions = () => {
    setClosedOrders(new Set());
  };

  const closeAllAccordions = () => {
    setClosedOrders(new Set(Object.keys(groupedData)));
  };

  const errObj = serviceAndRepairQuery?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const isPlaceholderVisible =
    !numberOfRows &&
    !serviceAndRepairQuery.isLoading &&
    !serviceAndRepairQuery.isPlaceholderData &&
    !isExternalServiceUnavailable;

  return (
    <div data-test-id={'service-and-repair-container'}>
      <Breadcrumbs />
      <Loading
        loading={
          serviceAndRepairQuery.isLoading ||
          serviceAndRepairQuery.isPlaceholderData
        }
      >
        <Filters {...filterProps} />
        {!isExternalServiceUnavailable && (
          <>
            <AccordionHeader
              desc={params.desc}
              sortBy={params.sortBy}
              i18n={i18n}
              columnsOfOuterTable={columnsOfOuterTable}
              hoveredHeader={hoveredHeader}
              changeSortTable={changeSortTable}
              setHoveredHeader={(value) => setHoveredHeader(value)}
            />
            <Accordion
              handleClickAccordion={handleClickAccordion}
              closedOrders={closedOrders}
              groupedData={groupedData}
              languages={languages.filter((language) => language !== 'all')}
              openAllAccordions={openAllAccordions}
              closeAllAccordions={closeAllAccordions}
            />
          </>
        )}
        {isExternalServiceUnavailable && (
          <AbasPlaceholder containerClassname={styles.marginTop152} />
        )}
        {isPlaceholderVisible && <TablePlaceholder />}
        <AppPagination {...paginationProps} />
      </Loading>
    </div>
  );
};
